// A light's tag (L1 …) on a canvas: the plan's and a ceiling plan's, one look.

import type { Vec2 } from '../shared/types';
import {
  LIGHT_AMBER,
  type LightSymbol,
  lightSymbolStrokes,
} from './lights';

/** A tag's box on screen (px). */
export interface TagBox { x: number; y: number; w: number; h: number }

/** A tag's box centred at `at` (screen px), as `paintLightTag` draws it. */
export function lightTagBox(ctx: CanvasRenderingContext2D, tag: string, at: Vec2): TagBox {
  ctx.font = 'bold 11px system-ui, sans-serif';
  const w = ctx.measureText(tag).width + 8, h = 15;
  return { x: at.x - w / 2, y: at.y - h / 2, w, h };
}

/** Paint a tag in `box`, boxed in the light's amber; a moved one's leader runs
 *  from its light (`from`, screen px) to the box's edge, a dot at the light. */
export function paintLightTag(ctx: CanvasRenderingContext2D, tag: string, box: TagBox, from: Vec2 | null): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.strokeStyle = ctx.fillStyle = LIGHT_AMBER;
  ctx.lineWidth = 1;
  const c = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
  if (from) {
    // Leave the box where the line from its middle to the light crosses its edge.
    const dx = from.x - c.x, dy = from.y - c.y;
    const k = Math.min(dx ? box.w / 2 / Math.abs(dx) : Infinity, dy ? box.h / 2 / Math.abs(dy) : Infinity);
    if (k < 1) {
      ctx.beginPath(); ctx.moveTo(c.x + dx * k, c.y + dy * k); ctx.lineTo(from.x, from.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(from.x, from.y, 2, 0, Math.PI * 2); ctx.fill();
    }
  }
  ctx.font = 'bold 11px system-ui, sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.beginPath(); ctx.roundRect(box.x, box.y, box.w, box.h, 3);
  ctx.fillStyle = 'rgba(255, 176, 32, 0.16)'; ctx.fill();
  ctx.stroke();
  ctx.fillStyle = LIGHT_AMBER;
  ctx.fillText(tag, c.x, c.y + 0.5);
  ctx.restore();
}

/** Paint a light's legend symbol (`lightSymbolStrokes`) in a `w` × `h` box
 *  centred at `c` (screen px), in `color`. */
export function paintLightSymbol(ctx: CanvasRenderingContext2D, sym: LightSymbol, c: Vec2, w: number, h: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1, h / 14);
  const dash = Math.max(2, h / 6);
  for (const st of lightSymbolStrokes(sym, w, h)) {
    ctx.setLineDash(st.dashed ? [dash, dash * 0.7] : []);
    ctx.beginPath();
    if (st.ring) ctx.arc(c.x + st.ring.c.x, c.y - st.ring.c.y, st.ring.r, 0, Math.PI * 2);
    else if (st.pts) {
      st.pts.forEach((p, i) => (i ? ctx.lineTo(c.x + p.x, c.y - p.y) : ctx.moveTo(c.x + p.x, c.y - p.y)));
      if (st.closed) ctx.closePath();
    }
    if (st.fill) ctx.fill(); else ctx.stroke();
  }
  ctx.restore();
}
